import * as THREE from 'three';
import { RGBELoader } from 'three/addons/loaders/RGBELoader';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader';
import { getProductDetails, extract3DModelInfo } from './get-product-model.js';
import { SmokeParticles } from './smoke-particles.js';
import { DistantSmoke } from './distant-smoke.js';
import { BackgroundSmoke } from './background-smoke.js';

// Import the PUB_SUB_EVENTS constant for cart updates
const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

class GalleryScene extends HTMLElement {
  constructor() {
    super();
    this.cartId = null;
    this.camera = null;
    this.scene = null;
    this.renderer = null;
    this.currentAngle = 0;
    this.targetAngle = 0;
    this.easels = [];
    this.paintings = [];
    this.isRotating = false;
    this.animate = this.animate.bind(this);
    this.smokeParticles = null; // New property for particle-based smoke
    this.galleryIndex = 1;
    this.lastTargetAngle = 8.527180059743724;
    this.distantSmoke = null; // New property for distant smoke effect
    this.backgroundSmoke = null; // New property for background smoke
  }

  // Helper function to wrap text
  wrapText(context, text, x, y, maxWidth, lineHeight) {
    // First, split by any explicit line breaks
    const paragraphs = text.split('\n');
    let yPos = y;
    let allLines = [];
    
    // Process each paragraph
    for (let i = 0; i < paragraphs.length; i++) {
      const words = paragraphs[i].split(' ');
      let line = '';
      
      // Test each word for fit
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = context.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > maxWidth && n > 0) {
          // If we exceed the width, create a new line
          allLines.push({ text: line, y: yPos });
          yPos += lineHeight;
          line = words[n] + ' ';
        } else {
          line = testLine;
        }
      }
      
      // Add the last line
      allLines.push({ text: line, y: yPos });
      yPos += lineHeight;
    }
    
    return { lines: allLines, newY: yPos };
  }

  connectedCallback() {
    this.init();
    this.animate();
    this.setupControls();
    window.addEventListener('click', this.handleMouseClick.bind(this)); // Add click event listener
  }

  async init() {
    const container = this.querySelector('.threejs-container');
    if (!container) return;

    // Scene setup
    this.scene = new THREE.Scene();

    // Camera at center
    if (!Modernizr.touchevents) {
      this.camera = new THREE.PerspectiveCamera(
        50,
        container.clientWidth / container.clientHeight,
        0.1,
        1000
      );
      this.camera.position.set(0, 1.4, 0); // Average human eye height
    } else {
      this.camera = new THREE.PerspectiveCamera(
        77.5,
        container.clientWidth / container.clientHeight,
        0.1,
        1000
      );
      this.camera.position.set(0, 1.2, 0); // Average human eye height
    }
    
    // Enable both layers for the camera (default layer 0 and smoke layer 1)
    this.camera.layers.enable(0); // Default layer
    this.camera.layers.enable(1); // Smoke layer

    // Renderer with physically correct lighting and alpha for better transparency
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true // Enable alpha for transparent background
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    container.appendChild(this.renderer.domElement);
    
    try {
      // Set scene background to pure black to provide maximum contrast with the smoke
      this.scene.background = new THREE.Color(0x000000);
      
      // Initialize the background smoke effect
      this.backgroundSmoke = new BackgroundSmoke(this.scene, this.camera);
      
      // Fetch products from Shopify
      const products = await this.fetchProducts();
      this.createEasels(products);

      this.buildRotationNav(products);

    } catch (error) {
      console.error('Error in initialization:', error);
      // Fallback background color if initialization fails
      this.scene.background = new THREE.Color(0x222222);
    }

    this.setupLighting();
    this.addFloor();

    // Handle window resize
    window.addEventListener('resize', () => {
      if (!container) return;
      
      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    });

    this.setupTouchControls();
  }

  async fetchProducts() {
    try {
      // First, fetch basic product data
      const response = await fetch('/products.json');
      const data = await response.json();
      
      // Group products by type (clothing vs paintings)
      const productsByType = {
        paintings: [],
        tshirts: []
      };
      
      // Create an array of promises to fetch metafields for each product
      const metafieldPromises = data.products.map(async (product) => {
        try {
          // Fetch metafields for this product using the Shopify metafields endpoint
          const metafieldsResponse = await fetch(`/products/${product.handle}?section_id=product-metafields`);
          const metafieldsHtml = await metafieldsResponse.text();
          
          // Parse the HTML to extract metafields
          const parser = new DOMParser();
          const doc = parser.parseFromString(metafieldsHtml, 'text/html');
          
          // Look for metafields data in the HTML
          let metafields = {};
          
          // First try the formatted metafields
          const metafieldsJson = doc.querySelector('[data-product-metafields]');
          if (metafieldsJson) {
            try {
              // Try to parse the metafields JSON
              metafields = JSON.parse(metafieldsJson.textContent);
            } catch (parseError) {
              console.error(`Error parsing metafields JSON for ${product.title}:`, parseError);
            }
          }
          
          // If the first approach didn't work, try raw metafields
          if (Object.keys(metafields).length === 0) {
            const rawMetafieldsJson = doc.querySelector('[data-raw-metafields]');
            if (rawMetafieldsJson) {
              try {
                const rawData = JSON.parse(rawMetafieldsJson.textContent);
                
                // Extract custom metafields from raw data
                const customMetafields = rawData.metafields_data.filter(m => m.namespace === 'custom');
                if (customMetafields.length > 0) {
                  metafields.custom = {};
                  customMetafields.forEach(field => {
                    metafields.custom[field.key] = field.value;
                  });
                }
              } catch (parseError) {
                console.error(`Error parsing raw metafields JSON for ${product.title}:`, parseError);
              }
            }
          }
          
          if (Object.keys(metafields).length === 0) {
          }
          
          // Add metafields to the product
          return {
            ...product,
            metafields
          };
        } catch (error) {
          console.error(`Error fetching metafields for ${product.title}:`, error);
          // Return the original product if we couldn't fetch metafields
          return product;
        }
      });
      
      // Wait for all metafield fetches to complete
      const productsWithMetafields = await Promise.all(metafieldPromises);
      
      // Process products with metafields
      productsWithMetafields.forEach(product => {
        const variant = product.variants[0];
        // Make sure variant ID is a number
        const variantId = parseInt(variant.id, 10);
        
        // Debug full product object
        
        // Find 3D model in media if it exists
        let model3dUrl = null;
        let model3dPreviewImage = null;
        
        // Check if there's a 3D model URL in metafields
        if (product.metafields && product.metafields.model && product.metafields.model.url) {
          model3dUrl = product.metafields.model.url;
        }
        
        // Check for preview image in metafields
        if (product.metafields && product.metafields.model && product.metafields.model.preview_image) {
          model3dPreviewImage = product.metafields.model.preview_image;
        }
        
        
        // Standard products.json doesn't include media info
        // Use our helper to fetch detailed product data for T-shirts
        if (product.product_type === 'T-shirt' || 
            product.tags?.includes('t-shirt') || 
            product.tags?.includes('tshirt') ||
            product.title.toLowerCase().includes('t-shirt') ||
            product.title.toLowerCase().includes('tshirt')) {
          
          // We'll attempt to load 3D models later for T-shirts
        }
        
        const productData = {
          image: product.images[0].src,
          model3dUrl: model3dUrl,
          model3dPreviewImage: model3dPreviewImage,
          title: product.title,
          handle: product.handle,
          variantId: variantId,
          price: variant.price,
          metafields: product.metafields,
          tags: product.tags || []
        };

        
        // Check if product is a t-shirt based on tags, product type, or title
        if (
          product.product_type === 'T-shirt' || 
          product.tags?.includes('t-shirt') || 
          product.tags?.includes('tshirt') ||
          product.title.toLowerCase().includes('t-shirt') ||
          product.title.toLowerCase().includes('tshirt')
        ) {
          productsByType.tshirts.push(productData);
        } else {
          productsByType.paintings.push(productData);
        }
      });
      
      
      return productsByType;
    } catch (error) {
      console.error('Error fetching products:', error);
      return { paintings: [], tshirts: [] };
    }
  }

  // Add a method to update the cart icon bubble manually
  async updateCartIconBubble() {
    try {
      // Fetch the current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update the cart count bubble
      const cartCountBubble = document.querySelector('.cart-count-bubble');
      
      if (!cartCountBubble && cart.item_count > 0) {
        // If bubble doesn't exist but should, fetch and replace the icon
        this.fetchAndUpdateCartIcon();
      } else if (cartCountBubble) {
        // If bubble exists, update the count
        const cartCountSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
        if (cartCountSpan) {
          cartCountSpan.textContent = cart.item_count;
        }
        
        // Update visually hidden count for screen readers
        const visibleHiddenSpan = cartCountBubble.querySelector('.visually-hidden');
        if (visibleHiddenSpan) {
          visibleHiddenSpan.textContent = `${cart.item_count} items`;
        }
        
        // Hide bubble if count is 0
        if (cart.item_count === 0) {
          cartCountBubble.style.display = 'none';
        } else {
          cartCountBubble.style.display = '';
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
  
  // Fetch and update the entire cart icon
  async fetchAndUpdateCartIcon() {
    try {
      const response = await fetch('/?section_id=cart-icon-bubble');
      const html = await response.text();
      
      // Parse HTML and extract the cart icon content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cartIconContent = doc.querySelector('.shopify-section');
      
      if (cartIconContent) {
        const currentCartIcon = document.getElementById('cart-icon-bubble');
        if (currentCartIcon && currentCartIcon.parentNode) {
          // Replace the existing icon with the new one
          currentCartIcon.parentNode.innerHTML = cartIconContent.innerHTML;
        }
      }
    } catch (error) {
      console.error('Error fetching cart icon:', error);
    }
  }

  // Add this method to your class
  setupLighting() {
    // Ambient light for overall scene
    const ambient = new THREE.AmbientLight(0x555555, 0.2);
    this.scene.add(ambient);

    // Key light from front
    const frontLight = new THREE.DirectionalLight(0xefefff, 1.2);
    frontLight.position.set(0, 2, 5);
    this.scene.add(frontLight);

    // Fill light from back
    const backLight = new THREE.DirectionalLight(0xefefff, 1.2);
    backLight.position.set(0, 2, -5);
    this.scene.add(backLight);

    // Side lights 
    const leftLight = new THREE.DirectionalLight(0xefefff, 1.2);
    leftLight.position.set(-5, 2, 0);
    this.scene.add(leftLight);

    const rightLight = new THREE.DirectionalLight(0xefefff, 1.2);
    rightLight.position.set(5, 2, 0);
    this.scene.add(rightLight);
    
    // Add subtle point lights for smoke visibility
    const smokeLight1 = new THREE.PointLight(0xddffff, 2, 40);
    smokeLight1.position.set(0, 2, 0);
    this.scene.add(smokeLight1);
    
    const smokeLight2 = new THREE.PointLight(0xddffff, 2, 40);
    smokeLight2.position.set(0, -2, 0);
    this.scene.add(smokeLight2);
  }

  addSpotlights() {
    const radius = 5; // Same as easel radius
    const numSpots = 6; // Same as number of easels

    for (let i = 0; i < numSpots; i++) {
      const angle = (i / numSpots) * Math.PI * 2;
      const x = Math.sin(angle) * radius;
      const z = Math.cos(angle) * radius;

      const spotlight = new THREE.SpotLight(0xffffff, 0.7); // Reduced intensity due to environment lighting
      spotlight.position.set(x, 1.5, z);
      spotlight.target.position.set(x * 0.8, 1.5, z * 0.8);
      spotlight.angle = Math.PI / 6;
      spotlight.penumbra = 0.2;
      spotlight.decay = 2;
      spotlight.distance = 35;

      this.scene.add(spotlight);
      this.scene.add(spotlight.target);
    }
  }

  createButtonSprite(text) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set larger canvas for button
    canvas.width = 450;
    canvas.height = 80;

    // Set button styles
    const borderWidth = 2;
    const borderRadius = 10;

    // Draw button outline with transparency 
    context.strokeStyle = 'rgba(255, 255, 255, 0)';
    context.lineWidth = borderWidth;
    context.beginPath();
    context.roundRect(
        borderWidth,  // Offset by half border width
        borderWidth,  // Offset by half border width
        canvas.width - borderWidth,  // Subtract border width
        canvas.height - borderWidth,  // Subtract border width
        borderRadius
    );
    context.stroke();

    // Draw button background with transparency
    context.fillStyle = 'rgba(0, 0, 0, 1)';
    context.beginPath();
    context.roundRect(
      borderWidth,  // Full border width offset
      borderWidth,  // Full border width offset
      canvas.width - borderWidth * 2,  // Subtract border width * 2
      canvas.height - borderWidth * 2,  // Subtract border width * 2
      borderRadius - borderWidth  // Adjust radius for border
    );
    context.fill();

    // Set text style
    const fontSize = 32;
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = 'white';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    // Add text shadow to make text readable without background
    context.shadowColor = 'rgba(0, 0, 0, 0.8)';
    context.shadowBlur = 8;
    context.shadowOffsetX = 0;
    context.shadowOffsetY = 0;
    
    // Draw text
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    // Create texture and sprite
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;

    const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true
    });

    const sprite = new THREE.Sprite(spriteMaterial);
    
    // Set scale (adjust as needed)
    const baseScale = 0.15;
    const aspectRatio = canvas.width / canvas.height;
    sprite.scale.set(baseScale * aspectRatio, baseScale, 1);

    return sprite;
  }

  // Method to create museum-style placard for a painting
  createMuseumPlacard(info) {
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    var canvasWidth = 0;
    var canvasHeight = 0;
    
    if (!Modernizr.touchevents) {
      // Set canvas dimensions for the placard - larger for better legibility
      canvasWidth = 800;
      canvasHeight = 500;
    } else {
      // Set canvas dimensions for the placard - larger for better legibility
      canvasWidth = 600;
      canvasHeight = 225;
    }
    
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Draw placard background - neutral beige/off-white color with subtle border
    context.fillStyle = '#f5f2e9'; // Light cream/beige color
    context.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Add subtle border
    context.strokeStyle = '#d3c7a6'; // Slightly darker than background
    context.lineWidth = 4;
    context.strokeRect(2, 2, canvasWidth-4, canvasHeight-4);
    
    var titleFontSize = 42; // Reduced to be closer to other text
    var detailsFontSize = 38;
    var padding = 35;


    if (!Modernizr.touchevents) {
      // Set up text styling - balanced font sizes
      titleFontSize = 42; // Reduced to be closer to other text
      detailsFontSize = 38;
      padding = 35;
      
    } else {
      titleFontSize = 22; // Reduced to be closer to other text
      detailsFontSize = 16;
      padding = 18;
    }
    
    let yPosition = padding + titleFontSize;

    // Draw title (artwork name) in quotes and italics - with text wrapping
    context.font = `italic bold ${titleFontSize}px Georgia`;
    context.fillStyle = '#000000';
    context.textAlign = 'left';
    
    // Use text wrapping for the title
    const titleText = `"${info.title}"`;
    const maxWidth = canvasWidth - (padding * 2); // Max width accounting for padding
    const lineHeight = titleFontSize * 1.1; // Slightly tighter line spacing for better appearance
    
    const wrappedTitle = this.wrapText(context, titleText, padding, yPosition, maxWidth, lineHeight);
    
    // Draw each line of the wrapped title
    wrappedTitle.lines.forEach(line => {
      context.fillText(line.text, padding, line.y);
    });
    
    // Update yPosition to after the wrapped text
    yPosition = wrappedTitle.newY + 10;
    
    // Draw artist name
    context.font = `${detailsFontSize}px Georgia`;
    context.fillStyle = '#000000';
    context.fillText(`Cristian Sanchez`, padding, yPosition);
    
    // Move down for details
    yPosition += detailsFontSize + 10;
    
    // Get medium from metafields if available
    let medium = 'Oil on canvas';
    if (info.metafields && info.metafields.custom && info.metafields.custom.medium) {
      // Handle both string and object metafield formats
      medium = typeof info.metafields.custom.medium === 'object' ? 
               info.metafields.custom.medium.value || info.metafields.custom.medium : 
               info.metafields.custom.medium;
    }
    
    // Draw medium/materials with text wrapping
    context.font = `${detailsFontSize-2}px Georgia`;
    context.fillStyle = '#000000';
    
    // Use the same text wrapping function for medium
    const wrappedMedium = this.wrapText(context, medium, padding, yPosition, maxWidth, detailsFontSize);
    
    // Draw each line of the wrapped medium text
    wrappedMedium.lines.forEach(line => {
      context.fillText(line.text, padding, line.y);
    });
    
    // Update yPosition to after the wrapped text
    yPosition = wrappedMedium.newY + 5;
    
    // Get dimensions from metafields if available
    let dimensions = '60 × 80 cm';
    if (info.metafields && info.metafields.custom && info.metafields.custom.measurements) {
      // Handle both string and object metafield formats
      dimensions = typeof info.metafields.custom.measurements === 'object' ? 
                  info.metafields.custom.measurements.value || info.metafields.custom.measurements : 
                  info.metafields.custom.measurements;
    }
    
    // Add dimensions with text wrapping
    const wrappedDimensions = this.wrapText(context, dimensions, padding, yPosition, maxWidth, detailsFontSize);
    
    // Draw each line of the wrapped dimensions text
    wrappedDimensions.lines.forEach(line => {
      context.fillText(line.text, padding, line.y);
    });
    
    // Update yPosition to after the wrapped text
    yPosition = wrappedDimensions.newY + 5;
    
    // Get year from metafields if available
    let year = '2023-2024';
    if (info.metafields && info.metafields.custom && info.metafields.custom.year_released) {
      // Handle both string and object metafield formats
      year = typeof info.metafields.custom.year_released === 'object' ? 
             info.metafields.custom.year_released.value || info.metafields.custom.year_released : 
             info.metafields.custom.year_released;
    }
    
    // Add year with text wrapping
    const wrappedYear = this.wrapText(context, year, padding, yPosition, maxWidth, detailsFontSize);
    
    // Draw each line of the wrapped year text
    wrappedYear.lines.forEach(line => {
      context.fillText(line.text, padding, line.y);
    });
    
    // Update yPosition to after the wrapped text
    yPosition = wrappedYear.newY + 5;
    
    // Add an accession number in small text (museum ID number)
    const smallFontSize = detailsFontSize - 8;
    context.font = `${smallFontSize}px Georgia`;
    context.fillText(`Accession #: CS-${Math.floor(Math.random() * 900) + 100}-${new Date().getFullYear()}`, padding, canvasHeight - padding);
    
    // Add price in subtle way at bottom
    yPosition += detailsFontSize;
    context.font = `bold ${detailsFontSize}px Georgia`;
    context.fillStyle = '#000000';
    if (info.price) {
      // Format price correctly with dollar sign
      const priceValue = parseInt(info.price);
      const formattedPrice = `$${priceValue.toLocaleString('en-US')}`;
      context.fillText(formattedPrice, padding, yPosition);
    }
    
    // Create a texture from the canvas
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;

    // Create a sprite material with the texture
    const spriteMaterial = new THREE.SpriteMaterial({ 
        map: texture,
        transparent: false,
    });

    // Create the sprite
    const sprite = new THREE.Sprite(spriteMaterial);
    
    // Calculate the aspect ratio of the placard
    const aspectRatio = canvas.width / canvas.height;
    
    // Set the scale maintaining the aspect ratio
    const baseScale = 0.5; // Much larger scale for better visibility
    sprite.scale.set(baseScale * aspectRatio, baseScale, 1);

    return { sprite, width: canvasWidth };
  }

  createEasels(productsByType) {
    const paintings = productsByType.paintings;
    const tshirts = productsByType.tshirts;
    
    // Calculate total number of positions (paintings + 1 for clothing rack)
    const count = paintings.length + (tshirts.length > 0 ? 1 : 0);
    const radius = 3;
    const easelGeometry = new THREE.BoxGeometry(0.1, 2, 0.1);
    const easelMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x8b4513,
      roughness: 0.7,
      metalness: 0.1
    });

    const easelHeightOffset = 1; // Adjust this value to raise/lower the easels
    const canvasHeightOffset = 0.1; // Adjust this value to raise/lower the canvases

    const canvasGeometry = new THREE.PlaneGeometry(1.5, 2);
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.sin(angle) * radius;
      const z = Math.cos(angle) * radius;
      
      // Determine if this position should be a clothing rack or an easel
      const isClothingRack = tshirts.length > 0 && i === count - 1;
      
      if (isClothingRack) {
        // Create clothing rack with t-shirts
        if (!Modernizr.touchevents) {
          this.createClothingRack(x + 0.35, z - 0.25, angle, tshirts);
        } else {
          this.createClothingRack(x + 0.5, z - 0.4, angle, tshirts);
        }
        continue;
      }
      
      // Create easel frame for paintings
      const easel = new THREE.Group();
      
      // Vertical supports
      const leftLeg = new THREE.Mesh(easelGeometry, easelMaterial);
      const rightLeg = new THREE.Mesh(easelGeometry, easelMaterial);
      leftLeg.position.x = -0.8;
      rightLeg.position.x = 0.8;
      easel.add(leftLeg);
      easel.add(rightLeg);

      // Horizontal support for canvas
      const support = new THREE.Mesh(
        new THREE.BoxGeometry(1.7, 0.1, 0.1),
        easelMaterial
      );
      support.position.y = -0.5;
      easel.add(support);

      // Position the easel
      easel.position.set(x, easelHeightOffset, z);
      easel.lookAt(0, 1, 0);
      this.scene.add(easel);
      this.easels.push(easel);

      // Load the painting image as a texture
      const paintingIndex = i;
      const { image, title, handle, variantId, price, metafields } = paintings[paintingIndex]; // Destructure image, title, metafields, and other properties
      new THREE.TextureLoader().load(image, (texture) => {
        
        // Get the original image dimensions
        const imgWidth = texture.image.width / 5;
        const imgHeight = texture.image.height / 5;

        
        console.log(imgWidth);
        console.log(imgHeight);
        var imgRatio = imgWidth / imgHeight;

        console.log(imgRatio);

        // Create canvas geometry with the original image dimensions
        const canvasGeometry = new THREE.PlaneGeometry(imgWidth / 250, imgHeight / 250); // Scale down for better visibility
        const canvasMaterial = new THREE.MeshStandardMaterial({
          map: texture,
          roughness: 0.9,     // Increased for less reflection
          metalness: 0.0,     // Keep at 0 for non-metallic appearance
          side: THREE.DoubleSide,
          envMapIntensity: 0.5  // Add this to reduce environment map influence
        });

        // Create the canvas mesh
        const canvas = new THREE.Mesh(canvasGeometry, canvasMaterial);
        canvas.position.y = imgHeight / 500 - (canvasHeightOffset * 5); // Adjust position based on height
        canvas.position.z = 0.1; // Position in front of the easel
        easel.add(canvas);
        this.paintings.push(canvas);


        // Create and position the museum placard for the painting
        const placardInfo = {
          title: title,
          price: price,
          metafields: metafields
        };
        const { sprite: placardSprite } = this.createMuseumPlacard(placardInfo);
        
        // Position the placard centered horizontally on mobile, keep original position on desktop
        var placardOffsetX = 0;
        if (!Modernizr.touchevents) {
          if (imgRatio > 1.1) {
            var placardOffsetY = -0.85; // Keep the vertical offset consistent
            placardOffsetX = -0.125;
          } else {
            var placardOffsetY = -0.85;
            placardOffsetX = -0.25;
          }
          
          if (imgHeight > 400) {
            var placardOffsetY = -1.5;
            placardOffsetX = -0.155;
          }

        } else {
          var placardOffsetY = -1;
        }
        var placardX = 0;
        var placardY = 0;
        
        if (Modernizr.touchevents) {
          // On mobile: Center horizontally (X=0), same Y position below painting
          placardY = placardOffsetY;
          placardX = 0; // Centered horizontally
        } else {
          // On desktop: Position at bottom right as before
          
          placardY = -(imgHeight / 500) - placardOffsetY;
          placardX = (imgWidth / 500) - placardOffsetX; // Right side of painting
        }
        
        placardSprite.position.set(placardX, placardY, 0.15);
        placardSprite.userData.isTitle = true;
        placardSprite.material.transparent = true;
        placardSprite.material.opacity = 0.15; // Default opacity of 0.15 when not being viewed directly
        placardSprite.material.depthTest = false;
        placardSprite.material.depthWrite = false; 
        // After adding the placard to the easel, explicitly set initial opacity
        easel.add(placardSprite); // Add the placard to the easel
        placardSprite.material.opacity = 0.15; // Ensure base opacity is applied immediately

        easel.userData.angle = angle;

        // Create "Add to Cart" button
        var addToCartButton = this.createButtonSprite('PURCHASE');
        addToCartButton.position.set(0, -0.65, 0.15); // Position below the painting
        easel.add(addToCartButton);

        // Create clickable area for the button
        const buttonClickArea = new THREE.Mesh(
            new THREE.PlaneGeometry(0.8, 0.2), // Adjust size as needed
            new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 })
        );
        buttonClickArea.position.copy(addToCartButton.position);
        
        easel.add(buttonClickArea);
        if (!Modernizr.touchevents) {
          buttonClickArea.position.z = buttonClickArea.position.z + 0.8;
          buttonClickArea.position.y = buttonClickArea.position.y + 0.25;
        } else {
          buttonClickArea.position.z = buttonClickArea.position.z + 0.1;
          buttonClickArea.position.y = buttonClickArea.position.y - 0.25;
        }
        
        // Store product info and click handler
        buttonClickArea.userData = {
            type: 'addToCart',
            productHandle: handle,
            productTitle: title
        };
        
        buttonClickArea.onClick = async () => {
          try {
              
              // Make sure variant ID is a number in case of string IDs
              const variantIdNumber = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
              
              const updatedCart = await this.addToCart(variantIdNumber);
              
              // Show success message

              // Change button text to indicate success
              this.updateButtonText(addToCartButton, 'ADDED!');
              
              // Dispatch event for the UI to update
              const event = new CustomEvent('addToCart', {
                  detail: {
                      handle: handle,
                      title: title,
                      success: true
                  }
              });
              document.dispatchEvent(event);
              
              // Optionally refresh the mini cart or show a success message
              if (window.refreshCart) {
                  window.refreshCart();
              }
          } catch (error) {
              console.error('Error adding to cart:', error);
              this.updateButtonText(addToCartButton, 'ERROR!');
              
              // Dispatch error event
              const event = new CustomEvent('addToCart', {
                  detail: {
                      handle: handle,
                      title: title,
                      success: false,
                      error: error.message
                  }
              });
              document.dispatchEvent(event);
          }
        };


      }, undefined, (error) => {
        console.error(`Error loading texture: ${error}`); // Log any errors
      });
    }

    this.lookAtClothingRack(); // Look at the clothing rack instead of first painting
  }

  formatMoney(amount) {
    return amount.toLocaleString('en-US');
  }
  
  addFloor() {

    var floorGeometry = '';
    
    if (!Modernizr.touchevents) {
      floorGeometry = new THREE.CircleGeometry(12, 32);
    } else {
      floorGeometry = new THREE.CircleGeometry(10, 32);
    }
    
    // Load floor texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('https://cdn.shopify.com/s/files/1/0912/7588/6902/files/green-grass-1024x1024.jpg', (texture) => {
      // Set texture properties
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(4, 4); // Adjust repeat as needed for proper scale

      // Create material with texture
      const floorMaterial = new THREE.MeshStandardMaterial({ 
        map: texture,
        roughness: 1,
        metalness: 0.1,
        side: THREE.DoubleSide
      });
      
      // Create and add floor mesh
      const floor = new THREE.Mesh(floorGeometry, floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = 0.01;
      this.scene.add(floor);
    }, 
    // onProgress callback
    undefined,
    // onError callback
    (error) => {
      console.error('Error loading floor texture:', error);
      
      // Fallback to simple colored material if texture fails to load
      const fallbackMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x333333, // Dark gray floor
        roughness: 1,
        metalness: 0,
        side: THREE.DoubleSide
      });
      
      const floor = new THREE.Mesh(floorGeometry, fallbackMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = 0.01;
      this.scene.add(floor);
    });
  }

  setupControls() {
    // Add arrow key and swipe controls
    document.addEventListener('keydown', (e) => {
      if (this.isRotating) return;

      switch (e.key) {
        case 'ArrowLeft':
          this.rotateTo(this.currentAngle + (Math.PI * 2 / this.easels.length));
          break;
        case 'ArrowRight':
          this.rotateTo(this.currentAngle - (Math.PI * 2 / this.easels.length));
          break;
      }
    });

    // Touch controls
    let touchStartX = 0;
    this.renderer.domElement.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    });

    this.renderer.domElement.addEventListener('touchend', (e) => {
      if (this.isRotating) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchEndX - touchStartX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.rotateTo(this.currentAngle + (Math.PI * 2 / this.easels.length));
        } else {
          this.rotateTo(this.currentAngle - (Math.PI * 2 / this.easels.length));
        }
      }
    });
  }

  setupTouchControls() {
    let touchStartX = 0;
    this.renderer.domElement.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    });

    this.renderer.domElement.addEventListener('touchend', (e) => {
      if (this.isRotating) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchEndX - touchStartX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.rotateTo(this.currentAngle + (Math.PI * 2 / this.easels.length));
        } else {
          this.rotateTo(this.currentAngle - (Math.PI * 2 / this.easels.length));
        }
      }
    });
  }

  rotateTo(targetAngle) {
    console.log(this.targetAngle);
    console.log(targetAngle);

    var spots = this.easels.length;
    console.log(spots);

    if (this.targetAngle < targetAngle) {
      if (this.galleryIndex <= (spots - 1)) {
        this.galleryIndex += 1;
      } else {
        this.galleryIndex = 1;
      }
    } else {
      if (this.galleryIndex > 0) {
        this.galleryIndex -= 1;
      } else {
        this.galleryIndex = spots;
      }
    }

    console.log('Easel number: ' + (this.galleryIndex));

    this.isRotating = true;
    this.targetAngle = targetAngle;

    

    // Don't reset opacities to zero anymore since we want a base opacity of 0.15
    // Just let the updateTitleOpacities method handle all opacity changes
    
    this.updateTitleOpacities();

    // Determine the index of the easel being navigated to
    var index = Math.round((targetAngle / (Math.PI * 2)) * this.easels.length) % this.easels.length;
    // if (index == 0) {
    //   index = this.easels.length - 1;
    // }
    $('#rot-nav .nav-dot').removeClass('active');
    $('#rot-nav .nav-dot:nth-of-type(' + (this.galleryIndex) + ')').addClass('active');
    
  }

  animate() {
    requestAnimationFrame(this.animate);

    if (this.isRotating) {
      const rotationSpeed = 0.1;
      const diff = this.targetAngle - this.currentAngle;
      
      if (Math.abs(diff) > 0.001) {
        this.currentAngle += diff * rotationSpeed;
        this.camera.rotation.y = this.currentAngle;
      } else {
        this.currentAngle = this.targetAngle;
        this.isRotating = false;
      }
    }

    // Update title opacities in every frame
    this.updateTitleOpacities();

    // Update background smoke animation
    if (this.backgroundSmoke) {
      this.backgroundSmoke.update();
    }

    // Clear renderer with pure black background for max contrast with smoke
    this.renderer.setClearColor(0x000000, 1);

    // Render the scene
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  // Method to update a painting texture
  updatePainting(index, imageUrl) {
    if (index >= 0 && index < this.paintings.length) {
      new THREE.TextureLoader().load(imageUrl, (texture) => {
        this.paintings[index].material = new THREE.MeshStandardMaterial({
          map: texture,
          roughness: 0.5,
          metalness: 0,
          side: THREE.DoubleSide
        });
      });
    }
  }

  disconnectedCallback() {
    if (this.renderer) {
      this.renderer.dispose();
    }

    // Clean up smoke particles
    if (this.smokeParticles) {
      this.smokeParticles.dispose();
      this.smokeParticles = null;
    }
    
    // Clean up distant smoke effect
    if (this.distantSmoke) {
      this.distantSmoke.dispose();
      this.distantSmoke = null;
    }
    
    // Clean up background smoke effect
    if (this.backgroundSmoke) {
      this.backgroundSmoke.dispose();
      this.backgroundSmoke = null;
    }
  }

  // Add a method to handle mouse clicks with layer filtering
  handleMouseClick(event) {
    const mouse = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();

    // Calculate mouse position in normalized device coordinates
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

    // Set raycaster to only detect objects on layer 0 (default layer)
    // This ensures smoke doesn't block clicks
    raycaster.layers.set(0);

    // Update the raycaster with the camera and mouse position
    raycaster.setFromCamera(mouse, this.camera);

    // Calculate objects intersecting the picking ray
    const intersects = raycaster.intersectObjects(this.scene.children, true);

    if (intersects.length > 0) {
      const clickedObject = intersects[0].object;
      if (clickedObject.onClick) {
        clickedObject.onClick(); // Call the click handler if it exists
      }
    }
  }

  async createCart() {
    const mutation = `
      mutation cartCreate {
        cartCreate {
          cart {
            id
            checkoutUrl
          }
        }
      }
    `;
  
    try {
      const response = await fetch('https://cristian-sanchez-art.myshopify.com/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '5d29fc03c2a3e6eb0f3569fdf6ab008a'
        },
        body: JSON.stringify({ query: mutation })
      });
  
      const data = await response.json();
      return data.data.cartCreate.cart;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }
  
  async addToCart(variantId, quantity = 1) {
    try {
      
      // Get cart notification or drawer component for sections
      const cartNotification = document.querySelector('cart-notification');
      const cartDrawer = document.querySelector('cart-drawer');
      const cartComponent = cartNotification || cartDrawer;
      let sectionsData = null;
      
      // Get sections to render if cart component exists
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        sectionsData = cartComponent.getSectionsToRender().map(section => section.section || section.id);
      }
      
      // Try different formats for the variant ID to handle various Shopify configurations
      // First attempt: Standard format with items array
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };
      
      // Add sections data if available
      if (sectionsData) {
        formData.sections = sectionsData;
        formData.sections_url = window.location.pathname;
      }
      
  
      // Make the request to Shopify's Cart API
      let response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      // If the first attempt fails with 422, try alternative format
      if (response.status === 422) {
        
        // Alternative format: directly using variant ID as a form parameter
        const alternativeFormData = new FormData();
        alternativeFormData.append('id', variantId);
        alternativeFormData.append('quantity', quantity);
        
        // Add sections to update when cart is updated
        if (sectionsData) {
          alternativeFormData.append('sections', sectionsData);
          alternativeFormData.append('sections_url', window.location.pathname);
        }
        
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json'
          },
          body: alternativeFormData
        });
      }
  
      // Log full response details for debugging
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Error details:', data);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // First, always update cart count
      this.updateCartIconBubble();
      
      // Specific handling for cart notification
      if (cartNotification && typeof cartNotification.renderContents === 'function') {
        try {
          // First, ensure we have the key for the cart notification
          if (data && !data.key && data.items && data.items.length > 0) {
            data.key = data.items[0].key;
          }
          
          cartNotification.renderContents(data);
          
          // Explicitly trigger open for the notification
          // Use a small delay to ensure everything is ready
          setTimeout(() => {
            if (typeof cartNotification.open === 'function') {
              cartNotification.open();
            }
          }, 100);
        } catch (err) {
          console.warn('Could not render cart notification:', err);
        }
      }
      // Handle cart drawer if that's what we have instead
      else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          cartDrawer.renderContents(data);
        } catch (err) {
          console.warn('Could not render cart drawer:', err);
        }
      }
      
      return data;
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  lookAtFirstPainting() {
    // Rotate 180 degrees (π radians) to face the first painting
    this.currentAngle = Math.PI;
    this.targetAngle = Math.PI;
    this.camera.rotation.y = this.currentAngle;
    
    // No animation needed
    this.isRotating = false;
    
    // Make sure opacity settings apply on initial load
    setTimeout(() => this.updateTitleOpacities(), 100);
  }
  
  // Method to look at the clothing rack on initial load
  lookAtClothingRack() {
    // Find the clothing rack in the easels array
    const clothingRack = this.easels.find(easel => easel.userData.isClothingRack);
    
    if (clothingRack) {
      // Get the stored angle from the clothing rack's userData
      // Adding Math.PI (180 degrees) since we need to face it directly
      const targetAngle = clothingRack.userData.angle + Math.PI;
      
      // Set camera to look at clothing rack
      this.currentAngle = targetAngle;
      this.targetAngle = targetAngle;
      this.camera.rotation.y = this.currentAngle;
      
      
      // No animation needed
      this.isRotating = false;
      
      // Make sure opacity settings apply on initial load
      setTimeout(() => this.updateTitleOpacities(), 100);
    } else {
      // Fallback to first painting if clothing rack not found
      this.lookAtFirstPainting();
    }
  }
  
  // Create a clothing rack with multiple t-shirts
  createClothingRack(x, z, angle, tshirts) {
    if (tshirts.length === 0) return;
    
    // Create a clothing rack group
    const clothingRack = new THREE.Group(); 

    if (!Modernizr.touchevents) {
      clothingRack.position.set(x, 0.45, z); // Position at floor level, model will have its own height
      clothingRack.lookAt(0, 0.5, 0);
    } else {
      clothingRack.position.set(x, 0.45, z); // Position at floor level, model will have its own height
      clothingRack.lookAt(0, 0.5, 0);
    }

    clothingRack.userData.angle = angle;
    clothingRack.userData.isClothingRack = true;
    this.scene.add(clothingRack);
    this.easels.push(clothingRack); // Add to easels array for rotation logic
    
    // Base dimensions for positioning t-shirts
    const baseWidth = 2.5;
    const baseHeight = 2.0;
    
    // Load the 3D clothing rack model
    const gltfLoader = new GLTFLoader();
    const modelUrl = 'https://cdn.shopify.com/3d/models/b8889b8a89c3ca08/clothes_rack_3d_models.glb';
    // const modelUrl = 'https://cdn.shopify.com/s/files/1/0912/7588/6902/files/clothing-rack.glb';
    
    // Create a loading indicator
    const loadingContainer = new THREE.Group();
    const loadingText = this.createTextSprite('Loading clothing rack...', 0.5, '#FFFFFF');
    loadingContainer.add(loadingText);
    loadingContainer.position.y = 1.5;
    clothingRack.add(loadingContainer);
    
    // Create a promise for loading the model
    const loadModelPromise = new Promise((resolve, reject) => {
      gltfLoader.load(
        modelUrl,
        (gltf) => {
          // Success callback
          loadingContainer.visible = false;
          
          const model = gltf.scene;
          
          // Scale and position the model appropriately
          model.scale.set(1, 1, 1); // Adjust scale as needed
          
          // Add the model to the clothing rack group
          clothingRack.add(model);
          
          // Apply materials and settings
          model.traverse((child) => {
            if (child.isMesh) {
              child.material.roughness = 0.7;
              child.material.metalness = 0.3;
              child.castShadow = false;
              child.receiveShadow = true;
            }
          });
          
          resolve(model);
        },
        // Progress callback
        (xhr) => {
          const percentage = Math.floor((xhr.loaded / xhr.total) * 100);
          loadingText.material.map.dispose();
          loadingText.material.map = this.createTextTexture(`Loading clothing rack... ${percentage}%`, 0.5, '#FFFFFF');
          loadingText.material.needsUpdate = true;
        },
        // Error callback
        (error) => {
          console.error('Error loading clothing rack model:', error);
          
          // Show error message
          loadingText.material.map.dispose();
          loadingText.material.map = this.createTextTexture('Error loading model. Using placeholder.', 0.5, '#FF0000');
          loadingText.material.needsUpdate = true;
          
          // Create a fallback placeholder after a short delay
          setTimeout(() => {
            loadingContainer.visible = false;
            this.createFallbackRack(clothingRack, baseWidth, baseHeight);
            resolve(null);
          }, 2000);
        }
      );
    });
    
    // If loading takes too long, show the fallback
    setTimeout(() => {
      loadModelPromise.then(model => {
        if (!model) {
          this.createFallbackRack(clothingRack, baseWidth, baseHeight);
        }
      });
    }, 10000); // 10 second timeout
    
    // Create a display area for t-shirts
    const tshirtContainer = new THREE.Group();
    tshirtContainer.position.y = baseHeight * 0.6; // Position at hanging height on the rack
    clothingRack.add(tshirtContainer);
    
    // No collection title placard - removed as requested
    
    // Display t-shirts in a row
    const spacing = baseWidth / (tshirts.length + 1);
    tshirts.forEach((tshirt, index) => {
      const offset = -baseWidth/2 + spacing * (index + 1);
      this.addTshirtToRack(tshirtContainer, tshirt, offset);
    });
    
    // Add an "View T-Shirts" button
    const viewButton = this.createButtonSprite('SHOP TEES');
    viewButton.position.set(0, 0.25, 0.15); // Position below the rack
    // clothingRack.add(viewButton);
    
    // Create clickable area for the button
    const buttonClickArea = new THREE.Mesh(
      new THREE.PlaneGeometry(0.8, 0.2),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 })
    );
    buttonClickArea.position.copy(viewButton.position);
    buttonClickArea.position.z += 0.5;
    buttonClickArea.position.y += 0.15;
    clothingRack.add(buttonClickArea);
    
    // Store t-shirt data and click handler
    buttonClickArea.userData = {
      type: 'viewTshirts',
      tshirts: tshirts
    };
    
    // Click handler for viewing t-shirts
    buttonClickArea.onClick = () => {
      // Here you can show a modal or navigate to t-shirt collection
      window.location.href = '/collections/t-shirts';
    };
  }
  
  // Create a fallback clothing rack if the 3D model fails to load
  createFallbackRack(clothingRack, baseWidth, baseHeight) {
    
    const rackMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x696969, // Dark gray
      roughness: 0.7,
      metalness: 0.3
    });
    
    // Horizontal bar
    const topBar = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, baseWidth, 16),
      rackMaterial
    );
    topBar.rotation.z = Math.PI / 2; // Rotate to horizontal position
    topBar.position.y = baseHeight * 0.8;
    clothingRack.add(topBar);
    
    // Vertical supports
    const leftSupport = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, baseHeight, 16),
      rackMaterial
    );
    leftSupport.position.set(-baseWidth/2, baseHeight/2, 0);
    clothingRack.add(leftSupport);
    
    const rightSupport = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, baseHeight, 16),
      rackMaterial
    );
    rightSupport.position.set(baseWidth/2, baseHeight/2, 0);
    clothingRack.add(rightSupport);
    
    // Base feet
    const baseFoot = new THREE.Mesh(
      new THREE.BoxGeometry(baseWidth * 0.7, 0.05, baseWidth/4),
      rackMaterial
    );
    baseFoot.position.y = 0.025; // Half the height of the base
    clothingRack.add(baseFoot);
  }
  
  // Create a text sprite for loading indicators or messages
  createTextSprite(text, size, color) {
    const texture = this.createTextTexture(text, size, color);
    const spriteMaterial = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(2, 0.5, 1);
    return sprite;
  }
  
  // Create a texture with text
  createTextTexture(text, size, color) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 512;
    canvas.height = 128;
    
    // Background - transparent
    context.fillStyle = 'rgba(0, 0, 0, 0.5)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Text style
    const fontSize = 36;
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    // Draw text
    context.fillText(text, canvas.width / 2, canvas.height / 2);
    
    // Create texture
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    return texture;
  }
  
  // Add a t-shirt to the clothing rack
  addTshirtToRack(container, tshirt, offsetX) {
    // Check if this is a T-shirt product that might have a 3D model
    const isTshirt = 
      tshirt.tags?.some(tag => 
        tag.toLowerCase().includes('t-shirt') || 
        tag.toLowerCase().includes('tshirt')
      ) ||
      tshirt.title.toLowerCase().includes('t-shirt') ||
      tshirt.title.toLowerCase().includes('tshirt');
      
    const has3dTag = tshirt.tags?.some(tag => 
      tag.toLowerCase().includes('3d') || 
      tag.toLowerCase().includes('model')
    );
    
    // Try loading 3D model for T-shirts, especially if they have 3D-related tags
    if (isTshirt && (has3dTag || tshirt.model3dUrl)) {
      this.addTshirtModelToRack(container, tshirt, offsetX);
    } else {
      // Fall back to 2D image for non-T-shirts or those without 3D model hints
      // this.add2DTshirtToRack(container, tshirt, offsetX);
    }
  }

  // New method to add a 3D model t-shirt to the rack
  async addTshirtModelToRack(container, tshirt, offsetX) {
    
    // Create a loading placeholder while the model loads
    const loadingGroup = new THREE.Group();
    loadingGroup.position.set(offsetX, 0, 0.1);
    container.add(loadingGroup);
    
    // Create a loading indicator
    const loadingText = this.createTextSprite('Loading 3D model...', 0.5, '#FFFFFF');
    loadingGroup.add(loadingText);
    
    // If there's a preview image, show it while loading
    if (tshirt.model3dPreviewImage) {
      new THREE.TextureLoader().load(tshirt.model3dPreviewImage, (texture) => {
        const tshirtWidth = 0.4;
        const tshirtHeight = 0.6;
        
        const previewGeometry = new THREE.PlaneGeometry(tshirtWidth, tshirtHeight);
        const previewMaterial = new THREE.MeshStandardMaterial({
          map: texture,
          transparent: true,
          side: THREE.DoubleSide,
          roughness: 0.8,
          metalness: 0.1,
          opacity: 0.7 // Semi-transparent to indicate it's loading
        });
        
        const previewMesh = new THREE.Mesh(previewGeometry, previewMaterial);
        previewMesh.position.set(0, -tshirtHeight/2 + 0.1, 0);
        loadingGroup.add(previewMesh);
      });
    }
    
    // If we don't have a model URL yet, try to fetch product details to get it
    // let modelUrl = tshirt.model3dUrl;
    let modelUrl = null;
    
    if (!modelUrl && tshirt.handle) {
      try {
        // Update loading text
        loadingText.material.map.dispose();
        loadingText.material.map = this.createTextTexture(`Fetching model data...`, 0.5, '#FFFFFF');
        loadingText.material.needsUpdate = true;
        
        // Fetch detailed product info to get media data
        const detailedProduct = await getProductDetails(tshirt.handle);

        
        if (detailedProduct) {
          const modelInfo = extract3DModelInfo(detailedProduct);
          
          if (modelInfo.model3dUrl) {
            modelUrl = modelInfo.model3dUrl;
          }
        }
      } catch (error) {
        console.error(`Error fetching product details for ${tshirt.title}:`, error);
      }
    }
    
    // If we still don't have a model URL, fall back to 2D image
    if (!modelUrl) {
      container.remove(loadingGroup);
      // this.add2DTshirtToRack(container, tshirt, offsetX);
      return;
    }
    

    // Add this debugging code in your addTshirtModelToRack function
    
    // Load the 3D model
    const gltfLoader = new GLTFLoader();
    gltfLoader.load(
      modelUrl,
      (gltf) => {
        // Success - model loaded
        
        // Remove loading placeholder
        container.remove(loadingGroup);
        
        // Create a group for the model
        const tshirtGroup = new THREE.Group();
        tshirtGroup.position.set(offsetX, 0, 0.45);
        container.add(tshirtGroup);
        
        // Add the model to the group
        const model = gltf.scene;
        
        // Scale and position the model appropriately
        // These values may need adjustment based on the actual model dimensions
        model.scale.set(2.5, 2.5, 2.5);
        model.position.y = -0.215; // Adjust vertical position
        
        // Add the model to the t-shirt group
        tshirtGroup.add(model);
        
        // Apply materials and ensure it looks good
        model.traverse((child) => {
          if (child.isMesh) {
            child.material.roughness = 0.8;
            child.material.metalness = 0.1;
            // child.material.depthTest = false;
            // child.material.depthWrite = false;
            child.castShadow = false;
            child.receiveShadow = true;
          }
        });
        
        // Create a hanger for the 3D model
        this.addHangerToGroup(tshirtGroup);
        
        // Make the model clickable
        const clickArea = new THREE.Mesh(
          new THREE.PlaneGeometry(0.5, 0.7),
          new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 })
        );
        clickArea.position.set(0, -0.45, 0.25);
        tshirtGroup.add(clickArea);
        
        // Add user data and click handler
        tshirtGroup.userData = {
          type: 'tshirt',
          productHandle: tshirt.handle,
          productTitle: tshirt.title,
          variantId: tshirt.variantId
        };
        
        // Add click handler for adding to cart
        clickArea.onClick = async () => {
          try {
            
            // Show visual feedback (slight bounce animation)
            const duration = 175; // milliseconds
            const startTime = Date.now();
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Simple bounce effect
              const bounce = Math.sin(progress * Math.PI) * 0.1;
              tshirtGroup.position.y = bounce;
              
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                tshirtGroup.position.y = 0; // Reset position
              }
            };
            animate();
            
            // await this.addToCart(tshirt.variantId);
            
            // Dispatch event for the UI to update
            // const event = new CustomEvent('addToCart', {
            //   detail: {
            //     handle: tshirt.handle,
            //     title: tshirt.title,
            //     success: true
            //   }
            // });
            // document.dispatchEvent(event);

            
            window.location.href = '/products/' + tshirt.handle;

          } catch (error) {
            console.error('Error adding to cart:', error);
          }
        };
        
        // Add price label
        this.addPriceLabel(tshirtGroup, tshirt);
      },
      // Progress callback
      (xhr) => {
        const percentage = Math.floor((xhr.loaded / xhr.total) * 100);
        loadingText.material.map.dispose();
        loadingText.material.map = this.createTextTexture(`Loading... ${percentage}%`, 0.5, '#FFFFFF');
        loadingText.material.needsUpdate = true;
      },
      // Error callback
      (error) => {
        console.error(`Error loading 3D model for ${tshirt.title}:`, error);
        
        // Fallback to 2D image on error
        container.remove(loadingGroup);
        // this.add2DTshirtToRack(container, tshirt, offsetX);
      }
    );
  }

  // Renamed original method to use as fallback
  add2DTshirtToRack(container, tshirt, offsetX) {
    // Load t-shirt image as texture
    new THREE.TextureLoader().load(tshirt.image, (texture) => {
      // Create a more realistic t-shirt shape with hanger
      const tshirtWidth = 0.4;
      const tshirtHeight = 0.6;
      
      // Create t-shirt group
      const tshirtGroup = new THREE.Group();
      
      // Create a plane for the t-shirt
      const tshirtGeometry = new THREE.PlaneGeometry(tshirtWidth, tshirtHeight);
      const tshirtMaterial = new THREE.MeshStandardMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide,
        roughness: 0.8,
        metalness: 0.1
      });
      
      // Add some natural variation to t-shirt positioning
      const randomAngle = (Math.random() - 0.5) * 0.1; // Small random rotation
      
      const tshirtMesh = new THREE.Mesh(tshirtGeometry, tshirtMaterial);
      tshirtMesh.position.set(0, -tshirtHeight/2 + 0.1, 0); // Position below hanger
      tshirtMesh.rotation.z = randomAngle;
      tshirtGroup.add(tshirtMesh);
      
      // Add hanger to group
      this.addHangerToGroup(tshirtGroup);
      
      // Position the entire t-shirt group
      tshirtGroup.position.set(offsetX, 0, 0.1); // Offset horizontally along the rack
      container.add(tshirtGroup);
      
      // For interaction, we'll use the t-shirt group as the target
      const tshirtObject = tshirtGroup;
      
      // Make t-shirt group clickable
      tshirtObject.userData = {
        type: 'tshirt',
        productHandle: tshirt.handle,
        productTitle: tshirt.title,
        variantId: tshirt.variantId
      };
      
      // Create a transparent clickable area over the t-shirt
      const clickArea = new THREE.Mesh(
        new THREE.PlaneGeometry(tshirtWidth, tshirtHeight),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 })
      );
      // Position click area over the t-shirt in the group
      clickArea.position.set(0, -tshirtHeight/2 + 0.1, 0.02);
      tshirtGroup.add(clickArea);
      
      // Add hover effect for the t-shirt
      const originalScale = tshirtGroup.scale.clone();
      const hoverScale = originalScale.clone().multiplyScalar(1.05);
      
      tshirtGroup.addEventListener('mouseover', () => {
        tshirtGroup.scale.copy(hoverScale);
      });
      
      tshirtGroup.addEventListener('mouseout', () => {
        tshirtGroup.scale.copy(originalScale);
      });
      
      // Add click handler
      clickArea.onClick = async () => {
        try {
          
          // Show visual feedback (slight bounce animation)
          const duration = 200; // milliseconds
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Simple bounce effect
            const bounce = Math.sin(progress * Math.PI) * 0.1;
            tshirtGroup.position.y = bounce;
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              tshirtGroup.position.y = 0; // Reset position
            }
          };
          animate();
          
          await this.addToCart(tshirt.variantId);
          
          // Dispatch event for the UI to update
          const event = new CustomEvent('addToCart', {
            detail: {
              handle: tshirt.handle,
              title: tshirt.title,
              success: true
            }
          });
          document.dispatchEvent(event);
        } catch (error) {
          console.error('Error adding to cart:', error);
        }
      };
      
      // Add price label
      this.addPriceLabel(tshirtGroup, tshirt);
    });
  }

  // Update title opacities based on viewing angle
  updateTitleOpacities() {
    this.easels.forEach(easel => {
      // Find all title sprites in this easel (can be multiple for t-shirts)
      const titleSprites = easel.children.filter(child => child.userData.isTitle);
      if (titleSprites.length === 0) return;

      // Calculate the normalized angle between camera's current angle and easel's angle
      const cameraAngle = ((this.currentAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const easelAngle = ((easel.userData.angle + Math.PI) % (Math.PI * 2));
      
      let angleDiff = Math.abs(cameraAngle - easelAngle);
      if (angleDiff > Math.PI) {
        angleDiff = Math.PI * 2 - angleDiff;
      }

      // Define viewing angles
      const fullViewAngle = Math.PI / 4;  // 45 degrees - full visibility
      const fadeAngle = Math.PI / 3;      // 60 degrees - start fading

      // Base opacity is 0.15, increased to 1.0 when viewed directly
      let opacity;
      if (angleDiff <= fullViewAngle) {
        // Fully visible when within 45 degrees
        opacity = 1.0;
      } else if (angleDiff <= fadeAngle) {
        // Fade from full opacity to base opacity between 45 and 60 degrees
        const fadeRatio = (angleDiff - fullViewAngle) / (fadeAngle - fullViewAngle);
        opacity = 0; // Fade down to 0
      } else {
        // No opacity beyond 60 degrees
        opacity = 0;
      }

      // Apply the calculated opacity to all title sprites in this easel
      titleSprites.forEach(textSprite => {
        // Smooth transition for opacity changes
        const currentOpacity = textSprite.material.opacity;
        const smoothFactor = 0.1; // Adjust this value to control fade speed (0.1 = smooth, 1 = instant)
        textSprite.material.opacity += (opacity - currentOpacity) * smoothFactor;
      });
    });
  }

  // Update button text
  updateButtonText(buttonSprite, newText) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas size based on new text
    const fontSize = 32;
    context.font = `bold ${fontSize}px Arial`;
    const textWidth = context.measureText(newText).width;
    canvas.width = textWidth + 20; // Add padding
    canvas.height = 80;

    // Draw button background with transparency
    context.fillStyle = 'rgba(0, 0, 0, 1)';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Add text shadow to make text readable without background
    context.shadowColor = 'rgba(0, 0, 0, 0.8)';
    context.shadowBlur = 8;
    context.shadowOffsetX = 0;
    context.shadowOffsetY = 0;
    
    // Draw text
    context.fillStyle = 'white';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(newText, canvas.width / 2, canvas.height / 2);

    // Create new texture and update the button sprite
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    buttonSprite.material.map = texture;
  }
  
  // Helper method to add a hanger to a t-shirt group
  addHangerToGroup(tshirtGroup) {
    const hangerMaterial = new THREE.MeshStandardMaterial({
      color: 0xDDDDDD,
      roughness: 0.5,
      metalness: 0.8
    });
    
    // Hanger hook
    const hangerHook = new THREE.Mesh(
      new THREE.TorusGeometry(0.03, 0.01, 8, 12, Math.PI),
      hangerMaterial
    );
    hangerHook.rotation.z = Math.PI;
    hangerHook.position.y = 0.15;
    hangerHook.position.z = 0;
    tshirtGroup.add(hangerHook);
    
    // Hanger top bar
    const hangerTop = new THREE.Mesh(
      new THREE.CylinderGeometry(0.01, 0.01, 0.32, 8), // Width adjusted to 0.32
      hangerMaterial
    );
    hangerTop.rotation.z = Math.PI/2;
    hangerTop.position.y = 0.06;
    hangerTop.position.z = 0;
    tshirtGroup.add(hangerTop);
  }
  
  // Simplified t-shirt label function - just title and price, smaller size, fully visible on load
  // Simplified t-shirt label function - just title and price, smaller size, fully visible on load
  addPriceLabel(tshirtGroup, tshirt) {
    // Get display title from metafields if available
    let displayTitle = tshirt.title;
    if (tshirt.metafields && tshirt.metafields.product && tshirt.metafields.product.display_title) {
      displayTitle = tshirt.metafields.product.display_title;
    }
    
    // Create a canvas for a simplified t-shirt placard
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Smaller placard size
    const canvasWidth = 220;
    const canvasHeight = 80;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    // Fill background - same style as painting placards
    context.fillStyle = '#f5f2e9'; // Light cream/beige color
    context.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Add subtle border
    context.strokeStyle = '#d3c7a6'; // Slightly darker than background
    context.lineWidth = 4;
    context.strokeRect(2, 2, canvasWidth-4, canvasHeight-4);
    
    // Set up text styling
    const titleFontSize = !Modernizr.touchevents ? 14 : 10;
    const priceFontSize = !Modernizr.touchevents ? 11 : 7;
    
    // Draw title in quotes and italics
    context.font = `italic bold ${titleFontSize}px Georgia`;
    context.fillStyle = '#000000';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    // Position title text in the upper portion of the placard
    const titleY = canvasHeight * 0.35;
    const titleText = `"${displayTitle}"`;
    
    // Use text wrapping if needed
    const maxWidth = canvasWidth - 30; // 15px padding on each side
    const wrappedTitle = this.wrapText(context, titleText, canvasWidth / 2, titleY, maxWidth, titleFontSize * 1.1);
    
    // Actually draw each line of wrapped text
    context.textAlign = 'center'; // Ensure text is centered
    wrappedTitle.lines.forEach(line => {
      context.fillText(line.text, canvasWidth / 2, line.y);
    });
    
    // Draw price at the bottom with dollar sign
    if (tshirt.price) {
      context.font = `bold ${priceFontSize}px Georgia`;
      context.fillStyle = '#000000';
      context.textAlign = 'center';
      
      // Explicitly add dollar sign in front of the price
      const priceValue = parseInt(tshirt.price);
      context.fillText('$' + priceValue.toLocaleString('en-US'), canvasWidth / 2, canvasHeight * 0.75);
    }
    
    // Create texture and sprite
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true
    });
    
    const sprite = new THREE.Sprite(spriteMaterial);
    
    // Calculate aspect ratio and set appropriate scale
    const aspectRatio = canvas.width / canvas.height;
    const baseScale = 0.2; // Smaller scale for t-shirt placards
    sprite.scale.set(baseScale * aspectRatio, baseScale, 1);
    
    // Position the placard below the t-shirt
    if (!Modernizr.touchevents) {
      // Desktop position
      sprite.position.set(0, -0.65, 0.15);
    } else {
      // Mobile position
      sprite.position.set(0, -0.75, 0.15);
    }
    
    // Set sprite properties
    sprite.userData.isTitle = true;
    sprite.material.transparent = true;
    sprite.material.opacity = 1.0; // Start fully visible
    
    // Add to t-shirt group
    tshirtGroup.add(sprite);
  }

  buildRotationNav(products) {
    $(products.paintings).each(function() {
      console.log($(this));
      $('#rot-nav').append('<div class="nav-dot"></div>');
    });
    $('#rot-nav').append('<div class="nav-dot"></div>');
    $('#rot-nav .nav-dot:first-of-type').addClass('active');
  }

}

customElements.define('gallery-scene', GalleryScene);